/**
 * Host OS — Dashboard. Command-centre landing screen: KPI strip, unfinished
 * drafts, quick actions and a preview of upcoming events. The full event list
 * lives on the Events screen; deeper tools live in the sidebar.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Calendar, Users, TrendingUp, Sparkles, Plus, Loader2, Plane, Repeat,
  Pencil, FileText, X, ChevronRight, CalendarClock, Megaphone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { API, useAuth } from "@/App";
import { authHeaders } from "@/lib/firebase";
import HostLayout from "@/components/host/HostLayout";
import { KpiCard, EventRow, formatRevenue } from "@/components/host/hostUi";

const TYPE_CREATE_PATH = { game: "/create/game", series: "/create/series", trip: "/create/trip" };

const QUICK_ACTIONS = [
  { label: "Schedule", desc: "Calendar & bookings", path: "/host/schedule", icon: CalendarClock, accent: "bg-blue-500/15 text-blue-600 dark:text-blue-300" },
  { label: "Customers", desc: "Your CRM of truth", path: "/host/customers", icon: Users, accent: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300" },
  { label: "Revenue", desc: "Earnings & payouts", path: "/host/revenue", icon: TrendingUp, accent: "bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-300" },
  { label: "Marketing", desc: "Automations & blasts", path: "/host/marketing", icon: Megaphone, accent: "bg-amber-500/15 text-amber-600 dark:text-amber-300" },
];

const HostHomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDrafts = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/drafts`, { headers });
      if (res.ok) setDrafts(await res.json());
    } catch (e) { /* ignore */ }
  }, []);

  const deleteDraft = useCallback(async (draft_id) => {
    if (!window.confirm("Discard this draft? This can't be undone.")) return;
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/drafts/${draft_id}`, { method: "DELETE", headers });
      if (res.ok) {
        setDrafts((prev) => prev.filter((d) => d.draft_id !== draft_id));
        toast.success("Draft discarded");
      }
    } catch (e) { toast.error("Couldn't delete draft"); }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/host/stats`, { headers });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error("host/stats", e); }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/host/events?status=upcoming&type=all`, { headers });
      if (res.ok) setEvents(await res.json());
    } catch (e) { console.error("host/events", e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchDrafts();
    fetchEvents();
  }, [fetchStats, fetchDrafts, fetchEvents]);

  const revenue = useMemo(() => formatRevenue(stats?.revenue_this_month), [stats]);
  const hasEvents = stats && stats.total_hosted_lifetime > 0;
  const preview = events.slice(0, 4);

  const handleShare = async (event) => {
    const url = `${window.location.origin}${event.url}`;
    try {
      if (navigator.share) await navigator.share({ title: event.title, url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); }
    } catch (e) { /* cancelled */ }
  };

  return (
    <HostLayout>
      <div data-testid="host-os-page">
        {/* Header */}
        <div className="mb-6">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Host OS</p>
          <h1 className="font-display text-3xl sm:text-4xl">
            Hey {(user?.name || "").split(" ")[0] || "there"} 👋
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Your command-centre for every game, series and trip you organise.
          </p>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <KpiCard
            label="Upcoming"
            value={stats ? stats.upcoming_events : "—"}
            sub={stats ? `${stats.upcoming_breakdown.games} games · ${stats.upcoming_breakdown.series} series · ${stats.upcoming_breakdown.trips} trips` : null}
            icon={Calendar}
          />
          <KpiCard
            label="Customers"
            value={stats ? stats.total_customers : "—"}
            sub="in your CRM"
            icon={Users}
            accent="bg-blue-500/15 text-blue-700 dark:text-blue-300"
          />
          <KpiCard
            label="Revenue · this month"
            testid="kpi-revenue"
            value={revenue.primary}
            sub={revenue.secondary}
            icon={TrendingUp}
            accent="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
          />
          <KpiCard
            label="Lifetime hosted"
            value={stats ? stats.total_hosted_lifetime : "—"}
            sub="games + series + trips"
            icon={Sparkles}
            accent="bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300"
          />
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="host-quick-actions">
          {QUICK_ACTIONS.map((a) => {
            const Icon = a.icon;
            return (
              <button
                key={a.path}
                onClick={() => navigate(a.path)}
                className="text-left"
                data-testid={`quick-action-${a.label.toLowerCase()}`}
              >
                <Card className="p-4 border-border hover:border-primary/40 transition-colors h-full">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${a.accent}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <p className="font-medium text-sm flex items-center gap-1">
                    {a.label} <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{a.desc}</p>
                </Card>
              </button>
            );
          })}
        </div>

        {/* Drafts */}
        {drafts.length > 0 && (
          <Card className="border-dashed border-border p-4 mb-6" data-testid="host-drafts-strip">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <h2 className="font-display text-lg">Unfinished drafts</h2>
              <span className="text-xs text-muted-foreground">({drafts.length})</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {drafts.map((d) => {
                const typeLabel = d.type === "trip" ? "Trip" : d.type === "series" ? "Series" : "Game";
                const typeCls = d.type === "trip"
                  ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                  : d.type === "series"
                    ? "bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300"
                    : "bg-blue-500/15 text-blue-700 dark:text-blue-300";
                return (
                  <div key={d.draft_id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/30 border border-border" data-testid={`host-draft-${d.draft_id}`}>
                    {d.image_url ? (
                      <img src={d.image_url} alt="" className="w-12 h-12 rounded-md object-cover shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded-md bg-muted flex items-center justify-center shrink-0">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-medium ${typeCls}`}>{typeLabel}</span>
                        {d.sport && <span className="text-[10px] text-muted-foreground capitalize">· {d.sport}</span>}
                      </div>
                      <p className="text-sm font-medium truncate">{d.title || "(Untitled)"}</p>
                      <p className="text-[11px] text-muted-foreground">
                        Saved {d.updated_at ? new Date(d.updated_at).toLocaleDateString() : "—"}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => navigate(`${TYPE_CREATE_PATH[d.type] || "/create/game"}?draft=${d.draft_id}`)} className="h-8 gap-1" data-testid={`host-draft-resume-${d.draft_id}`}>
                        <Pencil className="w-3.5 h-3.5" /> Resume
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => deleteDraft(d.draft_id)} className="h-8 w-8" aria-label="Discard" data-testid={`host-draft-discard-${d.draft_id}`}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Upcoming preview */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl">Upcoming offerings</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate("/host/events")} className="gap-1" data-testid="host-view-all-events">
            View all <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : preview.length === 0 ? (
          <Card className="p-8 sm:p-12 border-dashed border-border text-center" data-testid="host-empty">
            {!hasEvents ? (
              <>
                <div className="mx-auto w-12 h-12 rounded-2xl bg-primary/15 text-primary-ink flex items-center justify-center mb-3">
                  <Sparkles className="w-6 h-6" />
                </div>
                <h2 className="font-display text-2xl mb-1">Become a host</h2>
                <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
                  Organise one-off games, recurring leagues or multi-day retreats. We handle payments, attendees and reminders so you just bring the vibe.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <Button onClick={() => navigate("/create/game")} variant="outline" className="gap-1.5" data-testid="empty-create-game">
                    <Calendar className="w-4 h-4" /> Host a game
                  </Button>
                  <Button onClick={() => navigate("/create/series")} variant="outline" className="gap-1.5" data-testid="empty-create-series">
                    <Repeat className="w-4 h-4" /> Start a series
                  </Button>
                  <Button onClick={() => navigate("/create/trip")} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5" data-testid="empty-create-trip">
                    <Plane className="w-4 h-4" /> Host a trip
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">No upcoming events right now.</p>
                <Button variant="ghost" size="sm" onClick={() => navigate("/host/events")} className="mt-3">
                  Browse all events
                </Button>
              </>
            )}
          </Card>
        ) : (
          <div className="space-y-3">
            {preview.map((ev) => (
              <EventRow key={`${ev.type}-${ev.id}`} event={ev} onShare={handleShare} />
            ))}
          </div>
        )}
      </div>
    </HostLayout>
  );
};

export default HostHomePage;
